import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react'
import { Avatar, Icon, View } from '@/components'
import { createStyles } from '@codeleap/styles'
import { StaticImageData } from 'next/image'
import { theme } from '@/config'

interface AudioPlayerProps {
  audioUrl: string
  duration?: number
  avatarUrl?: string
  userName?: string
  waveformData?: number[]
  isMyMessage?: boolean
}

const BAR_COUNT = 48
const MIN_BAR_HEIGHT = theme.spacing.value(0.5)
const MAX_BAR_HEIGHT = theme.spacing.value(3)

const resampleWaveform = (data: number[], targetSize: number) => {
  if (!data.length) {
    return Array.from({ length: targetSize }, () => 0)
  }

  if (data.length === targetSize) {
    return [...data]
  }

  const step = data.length / targetSize

  return Array.from({ length: targetSize }, (_, index) => {
    const rawIndex = index * step
    const lowerIndex = Math.floor(rawIndex)
    const upperIndex = Math.min(data.length - 1, Math.ceil(rawIndex))
    const weight = rawIndex - lowerIndex
    const lowerValue = data[lowerIndex] ?? 0
    const upperValue = data[upperIndex] ?? lowerValue
    return lowerValue * (1 - weight) + upperValue * weight
  })
}

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${String(mins).padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

const AudioPlayerInner: React.FC<AudioPlayerProps> = ({ audioUrl, duration, avatarUrl, userName, waveformData, isMyMessage = false }) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [audioDuration, setAudioDuration] = useState(duration || 0)
  const audioRef = useRef<HTMLAudioElement>(null)
  const animationFrameRef = useRef<number>()

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const updateTime = () => {
      setCurrentTime(audio.currentTime)
      if (isPlaying) {
        animationFrameRef.current = requestAnimationFrame(updateTime)
      }
    }

    const handleLoadedMetadata = () => {
      setAudioDuration(audio.duration)
    }

    const handleEnded = () => {
      setIsPlaying(false)
      setCurrentTime(0)
    }

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('ended', handleEnded)

    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(updateTime)
    }

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('ended', handleEnded)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [isPlaying])

  const togglePlayPause = async () => {
    const audio = audioRef.current
    if (!audio) return

    try {
      if (isPlaying) {
        audio.pause()
        setIsPlaying(false)
      } else {
        emitPlayAudio(audioUrl)
        await audio.play()
        setIsPlaying(true)
      }
    } catch (error) {
      console.error('Audio playback failed:', error)
      setIsPlaying(false)
    }
  }

  const progressRatio = audioDuration > 0 ? currentTime / audioDuration : 0

  const handleSeek = useCallback(
    (event: any) => {
      const audio = audioRef.current
      if (!audio || !audioDuration) return

      const pointerEvent = 'nativeEvent' in event ? event.nativeEvent : event
      const target = (pointerEvent?.currentTarget || pointerEvent?.target) as Element | undefined
      const rect = target?.getBoundingClientRect()
      if (!rect) return

      const clientX = pointerEvent?.clientX ?? 0
      const offset = clientX - rect.left
      const ratio = Math.min(Math.max(offset / rect.width, 0), 1)
      const newTime = ratio * audioDuration

      audio.currentTime = newTime
      setCurrentTime(newTime)
    },
    [audioDuration],
  )

  const normalizedWaveform = useMemo(() => {
    const waveform =
      waveformData && waveformData.length > 0
        ? waveformData
        : Array.from({ length: BAR_COUNT }, () => Math.random())

    const resampled = resampleWaveform(waveform, BAR_COUNT)
    const clamped = resampled.map(value => {
      if (typeof value !== 'number' || Number.isNaN(value)) return 0
      return Math.min(1, Math.max(0, value))
    })
    const range = MAX_BAR_HEIGHT - MIN_BAR_HEIGHT
    return clamped.map(value => MIN_BAR_HEIGHT + value * range)
  }, [waveformData])

  const playedBars = Math.floor(progressRatio * normalizedWaveform.length)

  const styles = useMemo(() => createStyles(theme => {
    const primaryColor = isMyMessage ? theme.baseColors.onPrimarySolid500Heading : theme.baseColors.primarySolid500
    const secondaryColor = isMyMessage
      ? theme.baseColors.onPrimarySolid500Caption : theme.baseColors.onPrimarySolid50Caption
    const timeColor = isMyMessage ? theme.baseColors.onPrimarySolid500Caption : theme.colors.onPrimarySolid50Caption

    return {
      wrapper: {
        ...theme.presets.column,
        ...theme.presets.alignCenter,
        gap: theme.spacing.value(1),
        ...theme.spacing.paddingBottom(0),
        borderRadius: theme.radius.large,
        minWidth: 200,
        maxWidth: '100%',
        width: '100%',
        position: 'relative',
        boxSizing: 'border-box',

        [theme.media.down('tablet')]: {
          minWidth: 180,
          maxWidth: '100%',
        },
        [theme.media.down('tabletSmall')]: {
          minWidth: 160,
          maxWidth: '100%',
        },
      },
      contentWrapper: {
        ...theme.presets.row,
        ...theme.presets.alignCenter,
        gap: theme.spacing.value(1),
        width: '100%',
        minWidth: 0,
      },
      audio: {
        display: 'none',
      },
      avatarImage: {
        wrapper: {
          width: 48,
          height: 48,
          flexShrink: 0,
          [theme.media.down('tablet')]: {
            width: 40,
            height: 40,
          },
          [theme.media.down('tabletSmall')]: {
            width: 36,
            height: 36,
          },
        },
        image: {
          width: 48,
          height: 48,
          [theme.media.down('tablet')]: {
            width: 40,
            height: 40,
          },
          [theme.media.down('tabletSmall')]: {
            width: 36,
            height: 36,
          },
        },
      },
      playButton: {
        borderRadius: theme.radius.small,
        border: 'none',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        padding: 0,
      },
      playIcon: {
        color: isMyMessage ? theme.baseColors.onPrimarySolid500Heading : theme.baseColors.onPrimarySolid50HeadingSubtle,
        width: theme.values.iconSize?.[2] ?? 16,
        height: theme.values.iconSize?.[2] ?? 16,
      },
      waveformWrapper: {
        ...theme.presets.column,
        ...theme.presets.justifyCenter,
        flex: 1,
        gap: theme.spacing.value(0.5),
        minWidth: 0,
        overflow: 'hidden',
      },
      waveformBars: {
        ...theme.presets.row,
        ...theme.presets.alignCenter,
        gap: theme.spacing.value(0.25),
        height: theme.spacing.value(4),
        width: '100%',
        minWidth: 0,
        flexShrink: 1,
      },
      waveBar: {
        width: theme.spacing.value(0.25),
        borderRadius: theme.radius.small,
        transition: 'background 100ms ease',
      },
      waveBarActive: {
        backgroundColor: primaryColor,
      },
      waveBarInactive: {
        backgroundColor: secondaryColor,
      },
      timeWrapper: {
        ...theme.presets.row,
        ...theme.presets.justifyEnd,
      },
      timeText: {
        fontSize: 12,
        fontFamily: 'Inter',
        fontWeight: 400,
        lineHeight: theme.typography.defaults.lineHeight,
        color: timeColor,
      },
    }
  }), [isMyMessage])

  return (
    <div style={styles.wrapper}>
      <AudioManager
        audioUrl={audioUrl}
        isPlaying={isPlaying}
        audioRef={audioRef}
        setIsPlaying={setIsPlaying}
      />
      <audio ref={audioRef} src={audioUrl} preload='metadata' style={styles.audio} />

      <View style={styles.contentWrapper}>
        {/* Avatar - sempre renderiza, mesmo sem foto */}
        <Avatar
          name={userName}
          image={avatarUrl ? (avatarUrl as unknown as StaticImageData) : undefined}
          debugName='AudioPlayer:avatar'
          style={styles.avatarImage}
        />

        {/* Play/Pause button */}
        <button
          onClick={togglePlayPause}
          style={styles.playButton}
          type='button'
        >
          <Icon
            name={isPlaying ? 'pause-01' : 'play-01'}
            style={styles.playIcon}
            debugName='AudioPlayer:toggle'
          />
        </button>

        {/* Waveform and time */}
        <div style={styles.waveformWrapper}>
          <div
            style={styles.waveformBars}
            onPointerDown={handleSeek}
            onClick={handleSeek}
          >
            {normalizedWaveform.map((height, index) => {
              const isPlayed = index <= playedBars
              return (
                <div
                  key={index}
                  style={{
                    ...styles.waveBar,
                    height,
                    ...(isPlayed ? styles.waveBarActive : styles.waveBarInactive),
                  }}
                />
              )
            })}
          </div>
        </div>
      </View>

      {/* Time display */}
      <div style={styles.timeWrapper}>
        <span style={styles.timeText}>{formatTime(currentTime || audioDuration)}</span>
      </div>
    </div>
  )
}

const AudioManager = ({ audioUrl, isPlaying, audioRef, setIsPlaying }) => {
  const audioUrlRef = useRef(audioUrl)
  const isPlayingRef = useRef(isPlaying)
  audioUrlRef.current = audioUrl
  isPlayingRef.current = isPlaying

  useEffect(() => {
    const handleGlobalPause = (e: CustomEvent<{ playingUrl: string }>) => {
      if (e.detail.playingUrl !== audioUrlRef.current && isPlayingRef.current) {
        audioRef.current?.pause()
        setIsPlaying(false)
      }
    }

    window.addEventListener('audio:play', handleGlobalPause as EventListener)
    return () => window.removeEventListener('audio:play', handleGlobalPause as EventListener)
  }, [])

  return null
}

function emitPlayAudio(audioUrl: string) {
  window.dispatchEvent(new CustomEvent('audio:play', { detail: { playingUrl: audioUrl } }))
}

export const AudioPlayer = memo(AudioPlayerInner, (prev, next) => {
  return (
    prev.audioUrl === next.audioUrl &&
    prev.isMyMessage === next.isMyMessage &&
    prev.waveformData === next.waveformData &&
    prev.userName === next.userName &&
    prev.avatarUrl === next.avatarUrl &&
    prev.duration === next.duration
  )
})
